import { ColDef } from 'ag-grid-community';

/**
 * One empty-value convention for every grid (WAP-1643): text and object
 * columns show the em-dash placeholder, numeric columns show a subtle 0, and
 * both carry the `sensika-cell--empty` class (see _ag-grid-overrides.scss).
 * The em-dash here is the grid placeholder glyph, not punctuation.
 */
export const EMPTY_VALUE_PLACEHOLDER = '—';
export const EMPTY_CELL_CLASS = 'sensika-cell--empty';

/** null, undefined and the empty string. Zero is a value here; see isEmptyNumeric. */
export function isEmptyCellValue(value: unknown): boolean {
  return value == null || value === '';
}

/**
 * Numeric gaps look like zeros on purpose (a real 0 and "no data" render the
 * same subtle 0), so both count as empty for styling. The tooltip still
 * distinguishes them through isEmptyCellValue.
 */
export function isEmptyNumeric(value: unknown): boolean {
  return isEmptyCellValue(value) || value === 0 || value === '0';
}

/**
 * A timestamp the backend could not fill: null/'' and the unix epoch, which
 * arrives as 0, "0" or a 1970-01-01 date string when a materialized audit
 * column is still unset. Nothing in this app legitimately dates from 1970.
 */
export function isEmptyTimestamp(value: unknown): boolean {
  let empty = isEmptyCellValue(value);
  if (!empty) {
    if (typeof value === 'number') {
      empty = value <= 0;
    } else if (typeof value === 'string') {
      empty = value === '0' || /^(1970-01-01|01-01-1970)\b/.test(value);
    } else if (value instanceof Date) {
      empty = value.getTime() <= 0;
    }
  }
  return empty;
}

/** Right-aligned columns are the numeric ones (ag-grid.md, "Pick the column type"). */
export function isNumericColumn(colDef: ColDef | undefined): boolean {
  const type = colDef?.type;
  const types = Array.isArray(type) ? type : type ? [type] : [];
  return types.includes('rightAligned') || colDef?.cellDataType === 'number';
}
